import { Locale, Locales, useCookie, useRouter } from "@amzn/xb-hooks";
import { useConfig } from "../../hooks/useConfig";
import { PRE_AUTH_LOCALE_COOKIE_NAME, LOCALE_SEARCH_PARAM_NAME } from "../../utils/constants";

/* 
  This hook determines the pre-auth locale for the user.
  Pre-auth locale is determined in the following order of precedence:
  1. URL "lang-locale" search param
  2. "preauthLangLocale" Cookie (if present and valid).
  3. Browser Language
  4. return null if none of the above are found
*/
export const usePreAuthLocale = (): Locale | null => {
  const { supportedLocales } = useConfig(); // pull from config as the the context is being populated by the caller of this hook
  const [cookieCode, setCookieCode] = useCookie(PRE_AUTH_LOCALE_COOKIE_NAME);

  const { useSearchParams } = useRouter();
  const searchParams = useSearchParams();

  const localeLookup = Locales.lookupFor(supportedLocales);

  const urlLocale = localeLookup(searchParams.get(LOCALE_SEARCH_PARAM_NAME));
  const cookieLocale = localeLookup(cookieCode);

  // this is currently executed in Node so '?.' is not sufficient for this global

  const languages = (typeof window !== "undefined" && navigator?.languages) || undefined;
  const browserLocale = languages?.map((langCode) => localeLookup(langCode)).find((l) => l !== undefined);
  const resolvedLocale = urlLocale ?? cookieLocale ?? browserLocale ?? null;

  // we do not override cookieVal with urlLocale
  const urlResolvedLocale = localeLookup(cookieLocale?.code ?? urlLocale?.code);

  if (urlResolvedLocale && urlResolvedLocale !== resolvedLocale) {
    setCookieCode(urlResolvedLocale.code);
  } else if (resolvedLocale?.code && resolvedLocale?.code !== cookieCode) {
    /** update cookie using useCookie hook only if the cookie value doesnt match with the resolved locale */
    setCookieCode(resolvedLocale.code);
  }

  return resolvedLocale;
};
